import { Component } from '@theme/component';

/**
 * Indéscents — Défilé de flacons (section « indescents-bottle-marquee »).
 * Le défilement est 100 % CSS (piste dupliquée, translateX(-50%)). Ce composant :
 *  - clone la liste rendue par Liquid (copie aria-hidden, non focusable) pour fermer la boucle, puis pose
 *    `is-ready` qui active l'animation (le serveur ne rend qu'une liste : HTML et temps Liquid divisés par 2) ;
 *  - en mode « picker », dispatche `ind:coffret:pick` ({ detail: { title } }) sur document,
 *    écouté par <indescents-coffret-builder> ; sans configurateur sur la page → fiche produit (data-url) ;
 *  - pose la classe `is-static` quand il y a trop peu de flacons pour remplir la largeur
 *    (sinon la boucle laisserait un trou).
 */
const EVT_PICK = 'ind:coffret:pick';

class IndescentsBottleMarquee extends Component {
  #io = null;
  #ro;

  connectedCallback() {
    super.connectedCallback();
    this.#armImages();
    this.addEventListener('click', this.#onClick);
    this.#duplicate();
    this.#fit();
    if ('ResizeObserver' in window) {
      this.#ro = new ResizeObserver(() => this.#fit());
      this.#ro.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
    this.#io = null;
    this.removeEventListener('click', this.#onClick);
    this.#ro?.disconnect();
  }

  /**
   * Perf : les 55 flacons ne portent pas de `src` au rendu (section tout en bas de page).
   * On les charge quand la section approche de l'écran — clone compris, puisqu'on relit le DOM à ce moment-là.
   */
  #armImages() {
    const load = () => this.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
    if (!('IntersectionObserver' in window)) { load(); return; }
    this.#io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      this.#io?.disconnect();
      this.#io = null;
      load();
    }, { rootMargin: '600px 0px' });
    this.#io.observe(this);
  }

  hasBuilder() { return !!document.querySelector('indescents-coffret-builder'); }

  #onClick = (event) => {
    const btn = event.target.closest('[data-pick-title]');
    if (!btn || !this.contains(btn)) return;
    const title = (btn.dataset.pickTitle || '').trim();
    if (!title) return;

    if (!this.hasBuilder()) {
      if (btn.dataset.url) window.location.href = btn.dataset.url;
      return;
    }

    document.dispatchEvent(new CustomEvent(EVT_PICK, { detail: { title } }));

    // Retour visuel bref sur le flacon (et sa copie dupliquée)
    this.querySelectorAll('[data-pick-title]').forEach((el) => {
      if (el.dataset.pickTitle !== btn.dataset.pickTitle) return;
      el.classList.add('is-picked');
      clearTimeout(el._t);
      el._t = setTimeout(() => el.classList.remove('is-picked'), 1600);
    });
  };

  /** Ajoute la copie de la liste (une seule fois) nécessaire à la boucle CSS. */
  #duplicate() {
    const list = this.querySelector('[data-list]');
    if (!list || list.nextElementSibling?.matches('.ind-marquee__list[aria-hidden="true"]')) return;
    const copy = list.cloneNode(true);
    copy.removeAttribute('data-list');
    copy.removeAttribute('aria-label');
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a, button').forEach((el) => {
      el.setAttribute('tabindex', '-1');
      el.removeAttribute('aria-label');
    });
    list.after(copy);
    this.classList.add('is-ready');
  }

  #fit() {
    const list = this.querySelector('[data-list]');
    const viewport = this.querySelector('[data-viewport]');
    if (!list || !viewport) return;
    // Section en content-visibility:auto hors écran → pas encore de layout : on attend le ResizeObserver.
    if (!viewport.clientWidth) return;
    this.classList.toggle('is-static', list.scrollWidth <= viewport.clientWidth);
  }
}

if (!customElements.get('indescents-bottle-marquee')) customElements.define('indescents-bottle-marquee', IndescentsBottleMarquee);
